import DesignTokens from '@tableau/design-tokens';
import { TabStyles } from '@tableau/widgets';

import { cssClass, Style } from '~src/style';
import { applyColor } from '~src/style/ColorService';

const cssPrefix = 'ConnectToLLMDialog';
export namespace ConnectToLLMDialogStyles {

  export const formTable = cssClass(cssPrefix, 'form-table', {
    width: '100%'
  });

  export const descriptionText = cssClass(cssPrefix, 'description-text', {
    display: 'inline'
  });

  export const descriptionContainer = cssClass(cssPrefix, 'description-container', {
    marginBottom: TabStyles.Spacing.MediumSpace
  });

  export const inputStyle = cssClass(cssPrefix, 'input-style', {
    width: '100%'
  });

  // --- Show/hide visibility toggle (shared by the API-key and OAuth client-secret fields) ---

  // `as const` preserves the literal type for @tableau/icons' size union.
  export const TOGGLE_ICON_SIZE = 14 as const;
  const TOGGLE_ICON_PADDING = 3;
  const TOGGLE_HIT_AREA = TOGGLE_ICON_SIZE + TOGGLE_ICON_PADDING * 2;
  const TOGGLE_RIGHT_OFFSET = 6;
  const TOGGLE_RIGHT_PADDING = TOGGLE_HIT_AREA + TOGGLE_RIGHT_OFFSET + 2;
  // The API-key field renders no validation message, so its toggle is anchored from
  // the bottom (nothing below the input shifts it vertically).
  const TOGGLE_BOTTOM_OFFSET = 2;
  // The client-secret field DOES render a validation message below the input, which
  // would drag a bottom-anchored toggle downward when it appears. Anchor that toggle
  // from the top instead, over the input row, so it stays put in the error state.
  // These mirror @tableau/tableau-ui TextField's stacked geometry (LabelHeight /
  // FieldHeight); the label above the input is always present for this field.
  const FIELD_LABEL_HEIGHT = 21;
  const FIELD_INPUT_HEIGHT = 24;

  // Positioning context for a field plus its overlaid visibility toggle.
  export const visibilityToggleContainer = cssClass(cssPrefix, 'visibility-toggle-container', {
    position: 'relative',
    width: '100%'
  });

  // Reserve room on the right of the input so typed text never slides under the icon.
  export const visibilityToggleInputField = cssClass(cssPrefix, 'visibility-toggle-input-field', {
    width: '100%',
    $nest: {
      'input': {
        paddingRight: TOGGLE_RIGHT_PADDING
      }
    }
  });

  // Appearance shared by both toggle buttons; each field supplies its own vertical anchor.
  const toggleButtonBase: Style = {
    position: 'absolute',
    right: TOGGLE_RIGHT_OFFSET,
    width: TOGGLE_HIT_AREA,
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: 'transparent',
    border: 'none',
    padding: 0,
    cursor: 'pointer',
    color: 'inherit',
    borderRadius: TabStyles.Sizing.BorderRadius,
    $nest: {
      '&:focus-visible': {
        outline: '2px solid currentColor',
        outlineOffset: '1px'
      }
    }
  };

  export const apiKeyVisibilityToggle = cssClass(cssPrefix, 'api-key-visibility-toggle', toggleButtonBase, {
    bottom: TOGGLE_BOTTOM_OFFSET,
    height: TOGGLE_HIT_AREA
  });

  export const clientSecretVisibilityToggle = cssClass(cssPrefix, 'client-secret-visibility-toggle', toggleButtonBase, {
    top: FIELD_LABEL_HEIGHT,
    height: FIELD_INPUT_HEIGHT
  });

  // --- OAuth section styles (for Custom Gateway / LLM Gateway) ---

  export const oauthSectionHeader = cssClass(cssPrefix, 'oauth-section-header', {
    marginTop: TabStyles.Spacing.LargeSpace,
    marginBottom: TabStyles.Spacing.SmallSpace,
    paddingTop: TabStyles.Spacing.SmallSpace,
  });

  export const fileSelectionContainer = cssClass(cssPrefix, 'file-container', {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    boxSizing: 'border-box',
    width: '100%',
    height: 'auto',
    padding: '8px',
    marginTop: '4px',
    marginBottom: '8px',
    border: '2px dashed',
    position: 'relative'
  });

  export const dragAndDropChildren = cssClass(cssPrefix, 'inner-container', {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    width: '100%',
    zIndex: 1,
  });

  export const textMargin = cssClass(cssPrefix, 'text-margin', {
    marginTop: '0px',
    marginBottom: '8px',
    textAlign: 'center',
  });

  export const invalidFileColor = cssClass(cssPrefix, 'invalid-color', {
    color: applyColor(DesignTokens.color.font.default.error, TabStyles.Colors.AttentionDark),
  });

  export const validFileColor = cssClass(cssPrefix, 'valid-color', {
    color: applyColor(DesignTokens.color.feedback.default.success_subtle, '#00a370'),
  });

  export const defaultFileColor = cssClass(cssPrefix, 'default-color', {
    color: applyColor(DesignTokens.color.font.inverse.default, '#ccc'),
  });

  export const spinnerStyle = cssClass(cssPrefix, 'spinner-style', {
    lineHeight: 'normal',
    padding: 5,
    display: 'inline-block'
  });

}
